import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowRight, Film, Clock, Ticket, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Nav } from "@/components/festival/Nav";
import { Footer } from "@/components/festival/Footer";
import { getFullProgram, type PublicFilm } from "@/lib/films.functions";
import { getTicketsSettings, type TicketsSettings } from "@/lib/tickets.functions";

export const Route = createFileRoute("/tickets")({
  head: () => ({
    meta: [
      { title: "Tickets — Openluchtfilmfestival Apeldoorn" },
      {
        name: "description",
        content:
          "Bekijk alle films van editie 2026 en koop direct je tickets per avond.",
      },
      { property: "og:title", content: "Tickets — Openluchtfilmfestival Apeldoorn" },
      {
        property: "og:description",
        content:
          "Overzicht van alle avonden. Kies je film en bestel direct je tickets.",
      },
    ],
  }),
  component: TicketsPage,
});

const WEEKDAYS_NL = ["zondag", "maandag", "dinsdag", "woensdag", "donderdag", "vrijdag", "zaterdag"];
const MONTHS_NL = [
  "januari", "februari", "maart", "april", "mei", "juni",
  "juli", "augustus", "september", "oktober", "november", "december",
];

function parseDate(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

function formatLong(iso: string) {
  const d = parseDate(iso);
  return `${WEEKDAYS_NL[d.getDay()]} ${d.getDate()} ${MONTHS_NL[d.getMonth()]}`;
}

function slugify(title: string): string {
  return title
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function TicketsPage() {
  const [films, setFilms] = useState<PublicFilm[] | null>(null);
  const [tickets, setTickets] = useState<TicketsSettings>({ enabled: false, url: null });

  useEffect(() => {
    getFullProgram().then(setFilms).catch(() => setFilms([]));
    getTicketsSettings().then(setTickets).catch(() => {});
  }, []);

  const fallbackUrl =
    tickets.url && /^https?:\/\//i.test(tickets.url) ? tickets.url : null;

  return (
    <>
      <Nav />
      <main className="min-h-screen bg-night-deep pt-28 md:pt-32">
        <section className="relative py-12 md:py-16">
          <div className="mx-auto max-w-3xl px-5 text-center md:px-8">
            <p className="font-mono-cinema text-[10px] text-gold">— Tickets · editie 2026</p>
            <h1 className="mt-5 font-display text-4xl font-medium leading-[1.05] md:text-5xl lg:text-6xl">
              Kies je <span className="italic text-gold">avond</span>.
            </h1>
            <p className="mt-6 text-foreground/70 md:text-lg">
              Alle films op een rij. Klik op een avond om direct door te gaan
              naar de ticketverkoop.
            </p>
            <div className="mx-auto mt-8 flex max-w-2xl items-start gap-3 rounded-2xl border border-gold/30 bg-gold/5 p-4 text-left md:items-center md:p-5">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gold/15 text-gold">
                <Gift className="h-4 w-4" />
              </div>
              <p className="text-sm text-foreground/80">
                <span className="font-medium text-foreground">Bioscoopcadeau:</span>{" "}
                in september 2e kaartje gratis bij Cinemajestic Zutphen op vertoon
                van dit ticket. Geldig t/m 30 september 2026.
              </p>
            </div>
          </div>
        </section>

        <section className="relative pb-24 md:pb-32">
          <div className="mx-auto max-w-7xl px-5 md:px-8">
            {films === null ? (
              <p className="py-16 text-center font-mono-cinema text-[10px] text-foreground/60">
                Programma laden…
              </p>
            ) : films.length === 0 ? (
              <div className="mx-auto max-w-xl rounded-2xl border border-border bg-card p-10 text-center">
                <Film className="mx-auto h-10 w-10 text-gold/60" />
                <h2 className="mt-4 font-display text-2xl">
                  Het programma volgt binnenkort
                </h2>
                <p className="mt-3 text-sm text-foreground/70">
                  Zodra de films online staan verschijnen hier alle
                  ticket-links per avond.
                </p>
                <div className="mt-6 flex justify-center">
                  <Button asChild variant="hero">
                    <Link to="/">Terug naar home <ArrowRight className="h-4 w-4" /></Link>
                  </Button>
                </div>
              </div>
            ) : (
              <ul className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                {films.map((f) => {
                  const filmUrl =
                    f.cta_url && /^https?:\/\//i.test(f.cta_url) ? f.cta_url : null;
                  const url = filmUrl ?? fallbackUrl;
                  const label = f.cta_label?.trim() || "Koop tickets";
                  return (
                    <li
                      key={f.id}
                      className="film-grain group relative flex flex-col overflow-hidden rounded-2xl border border-border bg-card transition-all duration-500 hover:-translate-y-1 hover:border-gold/50 hover:shadow-glow"
                    >
                      <div className="relative aspect-[2/3] overflow-hidden border-b border-border bg-night-deep">
                        {f.poster_url && (
                          <img
                            src={f.poster_url}
                            alt=""
                            aria-hidden="true"
                            className="absolute inset-0 h-full w-full scale-110 object-cover blur-2xl opacity-40"
                          />
                        )}
                        {f.poster_url ? (
                          <img
                            src={f.poster_url}
                            alt={f.title}
                            loading="lazy"
                            className="relative h-full w-full object-contain transition-transform duration-700 group-hover:scale-[1.02]"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center">
                            <Film className="h-14 w-14 text-gold/40" />
                          </div>
                        )}
                      </div>

                      <div className="flex flex-1 flex-col p-6">
                        <p className="font-mono-cinema text-[10px] uppercase tracking-wider text-gold">
                          {formatLong(f.play_date)}
                        </p>
                        <h2 className="mt-2 font-display text-2xl leading-tight">
                          {f.title}
                        </h2>
                        {(f.genre || f.age_rating) && (
                          <p className="mt-1.5 text-sm text-foreground/75">
                            {[f.genre, f.age_rating ? `Kijkwijzer ${f.age_rating}` : null]
                              .filter(Boolean)
                              .join(" · ")}
                          </p>
                        )}

                        <div className="mt-5 flex items-center justify-between border-t border-border pt-4 text-sm text-foreground/70">
                          {f.start_time ? (
                            <span className="flex items-center gap-1.5">
                              <Clock className="h-3.5 w-3.5 text-gold" />
                              Doek op {f.start_time}
                            </span>
                          ) : <span />}
                          <Link
                            to="/films/$slug"
                            params={{ slug: slugify(f.title) }}
                            className="font-mono-cinema text-[10px] text-foreground/60 hover:text-gold"
                          >
                            Meer info →
                          </Link>
                        </div>

                        <div className="mt-5">
                          {url ? (
                            <Button asChild variant="hero" className="w-full">
                              <a
                                href={url}
                                target="_blank"
                                rel="noreferrer noopener"
                              >
                                <Ticket className="h-4 w-4" />
                                {label}
                              </a>
                            </Button>
                          ) : (
                            <Button variant="ghostLine" className="w-full" disabled>
                              Binnenkort te koop
                            </Button>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}

            <p className="mt-10 text-center font-mono-cinema text-[10px] text-foreground/60">
              ● Programma onder voorbehoud · Wijzigingen mogelijk
            </p>
            <p className="mt-3 text-center text-sm text-foreground/75">
              Liever aan de deur? <span className="text-gold">Tickets zijn ook aan de deur te koop</span> op de avond zelf, mits de voorstelling niet is uitverkocht.
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
